import {
  closestCenter,
  pointerWithin,
  type ClientRect,
  type Collision,
  type CollisionDetection,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { createContext, useContext } from 'react';
import type { SidebarRef, SidebarSection } from './sidebar-layout.util';
import type { TabsNavItem } from './use-tabs-nav.hook';

/** Where a dragged item comes from: a section of pins, or the tabs. */
export type DragZone = SidebarSection | 'tabs';

/** What the drag is over: an item in a zone, or a zone's empty space. */
export interface DragOver {
  zone: DragZone;
  /** The item's key, or null for the zone itself. */
  key: string | null;
}

/** Where a drop lands: in a section, before an item or at the end. */
export interface DropTarget {
  section: SidebarSection;
  /** The key of the item it goes before, or null for the end. */
  before: string | null;
}

/** What each draggable carries. */
export interface DragData {
  zone: DragZone;
  key: string;
  ref: SidebarRef;
  label: string;
  /** What the lifted item shows. */
  item: TabsNavItem;
  /** Lifted as a tile or as a row. */
  preview: 'tile' | 'row';
}

/** A draggable's id: its zone and its key, unique in the sidebar. */
export const dragId = (zone: DragZone, key: string) => `${zone}|${key}`;

/** How announcements name a section. */
export const SECTION_NAMES: Record<SidebarSection, string> = {
  favorites: 'Favorites',
  pinned: 'Pinned',
};

/** The id of a section's own drop area. */
export const zoneId = (section: SidebarSection) => `zone|${section}`;

/** Whether a droppable is a section's own drop area. */
export const isZone = (id: UniqueIdentifier) => String(id).startsWith('zone|');

/** The zone a droppable belongs to. */
export const zoneOf = (entry: { data: { current?: unknown } }) =>
  (entry.data.current as Partial<DragData> | undefined)?.zone;

/** A section's drop area, while it is empty: the only way into it. */
export const emptyZone = (
  id: UniqueIdentifier,
  sections: Record<SidebarSection, readonly string[]>
) => {
  const section = String(id).slice('zone|'.length) as SidebarSection;
  return sections[section]?.length === 0;
};

/**
 * The pin a pointer in a section's own space lands beside when it is over
 * no pin, such as the gap between two tiles: the nearest one. Null past the
 * section's last pin in reading order, which is its end.
 */
export function nearestPin(
  pointer: { x: number; y: number },
  pins: readonly { id: UniqueIdentifier; rect: ClientRect }[],
  last: ClientRect | undefined
): UniqueIdentifier | null {
  if (
    last &&
    (pointer.y > last.bottom ||
      (pointer.y >= last.top && pointer.x > last.right))
  )
    return null;
  let nearest: UniqueIdentifier | null = null;
  let best = Infinity;
  for (const { id, rect } of pins) {
    const dx = pointer.x - (rect.left + rect.width / 2);
    const dy = pointer.y - (rect.top + rect.height / 2);
    const distance = dx * dx + dy * dy;
    if (distance < best) {
      best = distance;
      nearest = id;
    }
  }
  return nearest;
}

/**
 * What a drag is over. With the pointer: the pin under it, else the pin
 * nearest to it in the section around it, else that section's end; tabs
 * take no drops. By keyboard: the pin the item was moved onto, or a
 * section while it is empty.
 */
export function sidebarCollisions(
  args: Parameters<CollisionDetection>[0],
  sections: Record<SidebarSection, readonly string[]>
): Collision[] {
  // A dragged tab stays over itself until it reaches a pin.
  const droppableContainers = args.droppableContainers.filter(
    (entry) => entry.id === args.active.id || zoneOf(entry) !== 'tabs'
  );
  const pointer = args.pointerCoordinates;
  if (pointer) {
    const hits = pointerWithin({ ...args, droppableContainers });
    const pin = hits.find((hit) => !isZone(hit.id));
    if (pin) return [pin];
    const zone = hits[0];
    if (!zone) return [];
    const section = String(zone.id).slice('zone|'.length) as SidebarSection;
    const keys = sections[section] ?? [];
    if (keys.length === 0) return [zone];
    const pins = droppableContainers.flatMap((entry) => {
      const rect = args.droppableRects.get(entry.id);
      return zoneOf(entry) === section && !isZone(entry.id) && rect
        ? [{ id: entry.id, rect }]
        : [];
    });
    const last = args.droppableRects.get(
      dragId(section, keys[keys.length - 1])
    );
    const nearest = nearestPin(pointer, pins, last);
    return nearest === null ? [zone] : [{ id: nearest }];
  }
  return closestCenter({
    ...args,
    droppableContainers: droppableContainers.filter(
      (entry) => !isZone(entry.id) || emptyZone(entry.id, sections)
    ),
  });
}

/**
 * Where a drop would land, or null when it would change nothing: over the
 * tabs, over the item's own place, or right where it already is. Over an
 * item, it lands before it, or after it when `after` (the pointer is past
 * the item's middle, or a keyboard move went down the list).
 */
export function dropTarget(
  sections: Record<SidebarSection, readonly string[]>,
  active: { zone: DragZone; key: string },
  over: DragOver | null,
  after: boolean
): DropTarget | null {
  if (!over || over.zone === 'tabs') return null;
  if (over.key === active.key) return null;
  const section = over.zone;
  const keys = sections[section].filter((key) => key !== active.key);
  let before: string | null = null;
  if (over.key !== null) {
    const index = keys.indexOf(over.key);
    if (index === -1) return null;
    before = after ? (keys[index + 1] ?? null) : over.key;
  }
  if (active.zone === section) {
    const list = sections[section];
    const next = list[list.indexOf(active.key) + 1] ?? null;
    if (before === next) return null;
  }
  return { section, before };
}

/** The zones from the top of the sidebar down. */
const ZONE_ORDER: Record<DragZone, number> = {
  favorites: 0,
  pinned: 1,
  tabs: 2,
};

/**
 * Whether a keyboard move lands after the item it is over: when it came
 * from above within its own section, and when it came up from a section
 * below. One step up from the rows lands at the end of the tiles.
 */
export function keyboardAfter(
  sections: Record<SidebarSection, readonly string[]>,
  active: { zone: DragZone; key: string },
  over: DragOver
): boolean {
  if (over.zone === 'tabs' || over.key === null) return false;
  if (active.zone !== over.zone)
    return ZONE_ORDER[active.zone] > ZONE_ORDER[over.zone];
  const list = sections[over.zone];
  return list.indexOf(active.key) < list.indexOf(over.key);
}

/** The drag under way, for the items that draw its drop line. */
export interface SidebarDragState {
  /** The dragged item's key. */
  activeKey: string | null;
  target: DropTarget | null;
}

export const SidebarDragContext = createContext<SidebarDragState>({
  activeKey: null,
  target: null,
});

export function useSidebarDrag(): SidebarDragState {
  return useContext(SidebarDragContext);
}

/**
 * Where an item draws the drop line: before it when the drop goes before
 * it, after it when it is the last item and the drop goes to the end.
 */
export function dropLineFor(
  target: DropTarget | null,
  section: SidebarSection,
  key: string,
  lastKey: string | null
): 'before' | 'after' | null {
  if (!target || target.section !== section) return null;
  if (target.before === key) return 'before';
  if (target.before === null && key === lastKey) return 'after';
  return null;
}
